/**
 * FC001P-45 — Trade Hub menu builder.
 *
 * One section, three steps, one state object. Every control writes to `state`
 * and every render reads from it, so "the preview updates without a save or a
 * refresh" is structural rather than a set of ad-hoc listeners.
 *
 * The draft is mirrored into sessionStorage on every mutation. That is what
 * makes the end-of-journey sign-in survivable: the customer leaves for
 * /account/login and comes back to a fully populated builder.
 *
 * Submission and logo persistence are deliberately behind `Transport`. Neither
 * has a decided destination on the ticket (open questions 4, 6 and 7), so the
 * whole journey is built against a seam instead of a guess.
 */
(function () {
  'use strict';

  var PRICE_RE = /^\d{1,4}(\.\d{1,2})?$/;
  var CREATE_YOUR_OWN = '__create_your_own__';

  function money(value) {
    var n = parseFloat(value);
    return isNaN(n) ? '' : n.toFixed(2);
  }

  /**
   * One key per draft, generated once and carried through sessionStorage
   * (including across the sign-in redirect) until a submission succeeds. The
   * backend does not exist yet, but whatever replaces `Transport.submitMenu`
   * needs an idempotency key from day one: a retried or double-fired request
   * must not create a second Menu Request record.
   */
  function newRequestKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'mb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* ---------------------------------------------------------------------- */
  /* Transport seam                                                          */
  /* ---------------------------------------------------------------------- */

  var Transport = {
    /**
     * Persist the venue logo and return a URL the print pipeline can read.
     * No destination is agreed yet, so the data URL is handed straight back:
     * the preview is correct and nothing downstream is invented.
     */
    uploadLogo: function (file, dataUrl) {
      return Promise.resolve({ url: dataUrl, name: file.name, size: file.size });
    },

    /**
     * Submit the completed menu. Resolves on success, rejects on failure; the
     * caller keeps the draft either way.
     */
    submitMenu: function (payload) {
      if (window.ThMenuBuilderTransport && typeof window.ThMenuBuilderTransport.submitMenu === 'function') {
        return window.ThMenuBuilderTransport.submitMenu(payload);
      }
      /* eslint-disable-next-line no-console */
      console.info('[menu-builder] submission payload (no transport configured)', payload);
      return Promise.reject(new Error('No submission transport configured'));
    }
  };

  /* ---------------------------------------------------------------------- */

  function MenuBuilder(root) {
    this.root = root;
    this.maxDrinks = parseInt(root.dataset.maxDrinks, 10) || 12;
    this.descriptionLimit = parseInt(root.dataset.descriptionLimit, 10) || 120;
    this.nameLimit = parseInt(root.dataset.nameLimit, 10) || 40;
    this.ingredientsLimit = parseInt(root.dataset.ingredientsLimit, 10) || 120;
    this.disclaimerLimit = parseInt(root.dataset.disclaimerLimit, 10) || 200;
    this.maxProducts = parseInt(root.dataset.maxProducts, 10) || 3;
    this.maxFlavours = parseInt(root.dataset.maxFlavours, 10) || 3;
    this.printUnderName = root.dataset.printUnderName || 'ingredients';
    this.printProducts = root.dataset.printProducts || 'preview';
    this.searchUrl = root.dataset.searchUrl || '/search';
    this.cocktailTag = (root.dataset.cocktailTag || '').trim();
    this.searchCache = {};
    this.maxPerOrder = parseInt(root.dataset.maxPerOrder, 10) || 50;
    this.storageKey = root.dataset.storageKey || 'th-menu-builder';
    this.loggedIn = root.dataset.loggedIn === 'true';

    this.cocktails = this.readCocktails();
    this.products = this.readJsonPayload('[data-mb-products]');
    this.flavours = this.readJsonPayload('[data-mb-flavours]');
    this.designs = this.readDesigns();

    this.state = this.load() || this.blankState();
    this.logoBlobUrl = null;

    this.cacheNodes();
    this.bind();
    this.restore();
  }

  MenuBuilder.prototype.blankState = function () {
    return {
      step: 1,
      requestKey: newRequestKey(),
      designId: '',
      logo: null,
      logoScale: 100,
      drinkCount: 0,
      flavourKey: false,
      drinks: [],
      side: 'front',
      quantity: 0,
      mixers: '',
      disclaimer: '',
      venue: { email: '', venue: '', address: '', postcode: '', country: '' },
      newsletter: { optIn: false, email: '' }
    };
  };

  MenuBuilder.prototype.blankDrink = function () {
    return {
      cocktailId: '',
      name: '',
      description: '',
      ingredients: '',
      price: '',
      products: [],
      flavours: [],
      custom: false
    };
  };

  /* ------------------------------- data --------------------------------- */

  MenuBuilder.prototype.readCocktails = function () {
    var node = this.root.querySelector('[data-mb-cocktails]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      /* A malformed cocktail payload must not take the builder down: the
         selector degrades to create-your-own only. */
      return [];
    }
  };

  /**
   * Products and flavour icons ship as JSON script tags, same as the
   * cocktails. A malformed payload degrades that one selector to empty rather
   * than taking the builder down, which is the existing contract.
   */
  MenuBuilder.prototype.readJsonPayload = function (selector) {
    var node = this.root.querySelector(selector);
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  /** Match Liquid's `handleize` closely enough to compare tags. */
  function handleize(value) {
    return String(value || '').toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /** Look an item up by id across a payload list. Ids arrive as numbers from
      Liquid and as strings from a restored draft, so compare loosely. */
  function findById(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(id)) return list[i];
    }
    return null;
  }

  MenuBuilder.prototype.readDesigns = function () {
    return Array.prototype.map.call(this.root.querySelectorAll('[data-mb-design]'), function (input) {
      var backs = [];
      try {
        backs = JSON.parse(input.dataset.designBacks || '[]');
      } catch (e) {
        /* A design with unreadable back artwork still has to be selectable;
           the preview falls back to the front face. */
        backs = [];
      }
      backs = backs.filter(function (b) { return b && b.src; });
      backs.sort(function (a, b) { return a.capacity - b.capacity; });

      return {
        id: input.value,
        name: input.dataset.designName || '',
        slug: input.dataset.designSlug || '',
        rowLayout: input.dataset.designRowLayout || 'stacked',
        logoPlacement: input.dataset.designLogoPlacement || 'front',
        front: input.dataset.designFront || '',
        backs: backs
      };
    });
  };

  /**
   * Each supplied template carries three back layouts, not one: the artwork
   * typesets a fixed number of drink rows, so a 4-drink menu on the 12-row
   * plate would print eight empty rules. Pick the smallest layout that holds
   * the count, and the largest available if the count overruns them all.
   *
   * The customer still chooses any number from 1 to 12, which is what the
   * ticket forbids restricting. The tiering is a rendering concern.
   */
  MenuBuilder.prototype.backFace = function (design, count) {
    if (!design || !design.backs.length) return '';
    for (var i = 0; i < design.backs.length; i++) {
      if (count <= design.backs[i].capacity) return design.backs[i].src;
    }
    return design.backs[design.backs.length - 1].src;
  };

  /**
   * Search the whole recipe corpus.
   *
   * The inline payload is only the 50 most recent articles: `blog.articles` is
   * hard-capped at 50 by Liquid and `limit:` cannot raise it, while the blog
   * holds 356. Anything beyond that page is reachable only through this call,
   * which hits an alternate search view returning matches AND their autofill
   * fields in one request, so picking a cocktail needs no second round trip.
   */
  MenuBuilder.prototype.searchCocktails = function (term) {
    var self = this;
    var key = term.toLowerCase();
    if (this.searchCache[key]) return Promise.resolve(this.searchCache[key]);

    var url = this.searchUrl + '?q=' + encodeURIComponent(term) +
              '&type=article&view=mb-json';

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('search responded ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        var out = (data.results || []).filter(function (r) {
          return self.cocktailAllowed(r);
        });
        self.searchCache[key] = out;
        return out;
      });
  };

  /** The search view cannot read the section's eligibility tag, so results
      carry their tags and the gate is applied here instead. */
  MenuBuilder.prototype.cocktailAllowed = function (result) {
    if (!this.cocktailTag) return true;
    var tags = String(result.tags || '').split('|').map(handleize);
    return tags.indexOf(handleize(this.cocktailTag)) !== -1;
  };

  MenuBuilder.prototype.design = function () {
    var id = this.state.designId;
    for (var i = 0; i < this.designs.length; i++) {
      if (this.designs[i].id === id) return this.designs[i];
    }
    return null;
  };

  /* ----------------------------- persistence ---------------------------- */

  MenuBuilder.prototype.save = function () {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (e) {
      /* A 2MB logo encodes to roughly 2.7MB of base64 and can push the draft
         past the sessionStorage quota. Losing the whole draft to save the logo
         is the wrong trade, so the logo is dropped from storage and kept in
         memory for this tab only. */
      var logo = this.state.logo;
      this.state.logo = null;
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (e2) {
        /* Storage is unavailable entirely (private mode, blocked cookies).
           The builder still works, it just will not survive the sign-in trip. */
      }
      this.state.logo = logo;
    }
  };

  MenuBuilder.prototype.load = function () {
    try {
      var raw = window.sessionStorage.getItem(this.storageKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      var base = this.blankState();
      Object.keys(base).forEach(function (key) {
        if (parsed[key] !== undefined) base[key] = parsed[key];
      });
      if (!Array.isArray(base.drinks)) base.drinks = [];
      base.drinks = base.drinks.map(this.normaliseDrink, this);
      return base;
    } catch (e) {
      return null;
    }
  };

  /**
   * Bring a restored drink up to the current shape. A draft saved before the
   * product selector and the approved icon set existed carries `flavour` as a
   * bare boolean and no lists at all; a true there recorded only "this drink
   * wants an icon", never which one, so it cannot be mapped to an id and is
   * dropped. Everything the customer actually typed survives untouched.
   */
  MenuBuilder.prototype.normaliseDrink = function (drink) {
    var base = this.blankDrink();
    if (!drink || typeof drink !== 'object') return base;

    Object.keys(base).forEach(function (key) {
      if (drink[key] !== undefined) base[key] = drink[key];
    });

    base.products = Array.isArray(base.products) ? base.products.map(String) : [];
    base.flavours = Array.isArray(base.flavours) ? base.flavours.map(String) : [];

    /* Drop ids whose product or icon the merchant has since retired, so a
       stale draft can never print something no longer approved. */
    base.products = base.products.filter(function (id) {
      return !!findById(this.products, id);
    }, this);
    base.flavours = base.flavours.filter(function (id) {
      return !!findById(this.flavours, id);
    }, this);

    base.products = base.products.slice(0, this.maxProducts);
    base.flavours = base.flavours.slice(0, this.maxFlavours);

    return base;
  };

  MenuBuilder.prototype.commit = function () {
    this.save();
    this.renderPreview();
    this.paintAllDrinkStates();
    this.syncGates();
  };

  /**
   * Repaint the completion ticks without rebuilding the accordions. A full
   * renderDrinks() on every keystroke would destroy the focused input, so the
   * tick is painted from state instead.
   */
  MenuBuilder.prototype.paintAllDrinkStates = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-drink]'), function (item) {
      var drink = self.state.drinks[parseInt(item.dataset.mbDrink, 10)];
      if (drink) self.paintDrinkState(item, drink);
    });
  };

  /* ------------------------------- nodes -------------------------------- */

  MenuBuilder.prototype.cacheNodes = function () {
    var r = this.root;
    this.nodes = {
      steps: r.querySelectorAll('[data-mb-step]'),
      split: r.querySelector('[data-mb-split]'),
      drinks: r.querySelector('[data-mb-drinks]'),
      count: r.querySelector('[data-mb-count]'),
      flavourKey: r.querySelector('[data-mb-flavour-key]'),
      disclaimerInput: r.querySelector('[data-mb-disclaimer-input]'),
      disclaimerCounter: r.querySelector('[data-mb-disclaimer-counter]'),
      disclaimer: r.querySelector('[data-mb-disclaimer]'),
      logoInput: r.querySelector('[data-mb-logo]'),
      logoLabel: r.querySelector('[data-mb-upload-label]'),
      logoClear: r.querySelector('[data-mb-logo-clear]'),
      art: r.querySelector('[data-mb-art]'),
      logoSlot: r.querySelector('[data-mb-logo-slot]'),
      logoMask: r.querySelector('[data-mb-logo-mask]'),
      logoSizeField: r.querySelector('[data-mb-logo-size-field]'),
      logoScale: r.querySelector('[data-mb-logo-scale]'),
      logoScaleOut: r.querySelector('[data-mb-logo-scale-out]'),
      slots: r.querySelector('[data-mb-slots]'),
      key: r.querySelector('[data-mb-key]'),
      overflow: r.querySelector('[data-mb-overflow]'),
      sides: r.querySelectorAll('[data-mb-side]'),
      submit: r.querySelector('[data-mb-submit]'),
      success: r.querySelector('[data-mb-success]'),
      download: r.querySelector('[data-mb-download]'),
      failure: r.querySelector('[data-mb-failure]')
    };
  };

  MenuBuilder.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var goto = event.target.closest('[data-mb-goto]');
      if (goto && self.root.contains(goto)) {
        event.preventDefault();
        self.goTo(parseInt(goto.dataset.mbGoto, 10));
        return;
      }

      var side = event.target.closest('[data-mb-side]');
      if (side) {
        self.state.side = side.dataset.mbSide;
        self.commit();
        self.paintSides();
        return;
      }

      var qty = event.target.closest('[data-mb-qty]');
      if (qty) {
        self.state.quantity = parseInt(qty.dataset.mbQty, 10);
        self.commit();
        self.paintQuantity();
        self.clearError('quantity');
        return;
      }

      var mixers = event.target.closest('[data-mb-mixers]');
      if (mixers) {
        self.state.mixers = mixers.dataset.mbMixers;
        self.commit();
        self.paintMixers();
        self.clearError('mixers');
      }
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-design]'), function (input) {
      input.addEventListener('change', function () {
        self.state.designId = input.value;
        self.commit();
      });
    });

    if (this.nodes.count) {
      this.nodes.count.addEventListener('change', function () {
        self.setDrinkCount(parseInt(this.value, 10) || 0);
      });
    }

    if (this.nodes.flavourKey) {
      this.nodes.flavourKey.addEventListener('click', function () {
        self.state.flavourKey = !self.state.flavourKey;
        this.setAttribute('aria-checked', String(self.state.flavourKey));
        this.classList.toggle('is-on', self.state.flavourKey);
        self.renderDrinks();
        self.commit();
      });
    }

    if (this.nodes.disclaimerInput) {
      this.nodes.disclaimerInput.addEventListener('input', function () {
        self.state.disclaimer = this.value;
        if (self.nodes.disclaimerCounter) {
          self.nodes.disclaimerCounter.textContent = this.value.length + '/' + self.disclaimerLimit;
        }
        self.clearError('disclaimer');
        self.commit();
      });
    }

    if (this.nodes.logoInput) {
      this.nodes.logoInput.addEventListener('change', function () {
        self.handleLogo(this.files && this.files[0]);
      });
    }

    if (this.nodes.logoScale) {
      this.nodes.logoScale.addEventListener('input', function () {
        self.state.logoScale = self.clampLogoScale(parseInt(this.value, 10));
        self.commit();
      });
    }

    if (this.nodes.logoClear) {
      this.nodes.logoClear.addEventListener('click', function () {
        self.state.logo = null;
        self.state.logoScale = 100;
        if (self.nodes.logoInput) self.nodes.logoInput.value = '';
        self.paintLogo();
        self.commit();
      });
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-venue]'), function (input) {
      input.addEventListener('input', function () {
        self.state.venue[input.dataset.mbVenue] = input.value;
        self.save();
        self.clearError(input.dataset.mbVenue);
      });
    });

    var optIn = this.root.querySelector('[data-mb-news-optin]');
    if (optIn) {
      optIn.addEventListener('change', function () {
        self.state.newsletter.optIn = this.checked;
        self.save();
      });
    }

    var newsEmail = this.root.querySelector('[data-mb-news-email]');
    if (newsEmail) {
      newsEmail.addEventListener('input', function () {
        self.state.newsletter.email = this.value;
        self.save();
      });
    }

    if (this.nodes.submit) {
      this.nodes.submit.addEventListener('click', function () {
        self.submit();
      });
    }

    /* A dropdown left open when focus moves elsewhere is a mobile trap. Only
       checking `root.contains` closed a combo solely when the click landed
       outside the whole builder — clicking any other field inside it left
       the open dropdown exactly where it was, since that click was still
       "inside root". Checking against each open combo individually closes it
       for a click anywhere else, while a click on that combo's own trigger
       or panel still reaches its own toggle/pick handlers first. */
    document.addEventListener('click', function (event) {
      var openCombos = self.root.querySelectorAll('.th-mb__combo.is-open');
      for (var i = 0; i < openCombos.length; i++) {
        if (!openCombos[i].contains(event.target)) {
          self.closeAllDropdowns();
          break;
        }
      }
    });
  };

  /* ------------------------------ restore ------------------------------- */

  MenuBuilder.prototype.restore = function () {
    var self = this;

    if (this.state.designId) {
      var input = this.root.querySelector('[data-mb-design][value="' + this.state.designId + '"]');
      if (input) {
        input.checked = true;
      } else {
        /* The chosen design was deactivated in the admin while a draft was
           open. Drop the selection rather than previewing missing artwork. */
        this.state.designId = '';
      }
    }

    if (this.nodes.count && this.state.drinkCount) {
      this.nodes.count.value = String(this.state.drinkCount);
    }

    if (this.nodes.flavourKey) {
      this.nodes.flavourKey.setAttribute('aria-checked', String(this.state.flavourKey));
      this.nodes.flavourKey.classList.toggle('is-on', this.state.flavourKey);
    }

    if (this.nodes.disclaimerInput) {
      this.nodes.disclaimerInput.value = this.state.disclaimer || '';
      if (this.nodes.disclaimerCounter) {
        this.nodes.disclaimerCounter.textContent = (this.state.disclaimer || '').length + '/' + this.disclaimerLimit;
      }
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-venue]'), function (input) {
      var key = input.dataset.mbVenue;
      if (self.state.venue[key]) input.value = self.state.venue[key];
      else self.state.venue[key] = input.value;
    });

    var optIn = this.root.querySelector('[data-mb-news-optin]');
    if (optIn) optIn.checked = !!this.state.newsletter.optIn;

    var newsEmail = this.root.querySelector('[data-mb-news-email]');
    if (newsEmail) {
      if (this.state.newsletter.email) newsEmail.value = this.state.newsletter.email;
      else this.state.newsletter.email = newsEmail.value;
    }

    this.renderDrinks();
    this.paintLogo();
    this.paintQuantity();
    this.paintMixers();
    this.paintSides();

    /* Coming back from /account/login lands on step 3 with everything intact. */
    var target = this.state.step || 1;
    if (target > 1 && !this.state.designId) target = 1;
    this.goTo(target, true);
  };

  /* ------------------------------ routing ------------------------------- */

  MenuBuilder.prototype.goTo = function (step, silent) {
    if (!silent) {
      if (step === 2 && !this.state.designId) return;
      if (step === 3) {
        if (!this.allDrinksValid(true)) return;
        if (!this.disclaimerValid()) {
          this.showError('disclaimer', 'Add a disclaimer before saving your menu.');
          return;
        }
      }
    }

    this.state.step = step;
    this.save();

    Array.prototype.forEach.call(this.nodes.steps, function (node) {
      node.hidden = parseInt(node.dataset.mbStep, 10) !== step;
    });

    if (this.nodes.split) this.nodes.split.hidden = step === 1;

    this.renderPreview();
    this.syncGates();

    if (!silent) {
      var top = this.root.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: top > 0 ? top : 0, behavior: 'smooth' });
    }
  };

  MenuBuilder.prototype.syncGates = function () {
    var toStep2 = this.root.querySelector('[data-mb-goto="2"].th-mb__btn');
    if (toStep2) toStep2.disabled = !this.state.designId;

    var toStep3 = this.root.querySelector('[data-mb-goto="3"].th-mb__btn');
    if (toStep3) toStep3.disabled = !this.allDrinksValid(false) || !this.disclaimerValid();

    if (this.nodes.submit) {
      this.nodes.submit.disabled = !this.loggedIn;
      this.nodes.submit.title = this.loggedIn ? '' : 'Sign in to submit your menu request.';
    }
  };

  /* ------------------------------- logo --------------------------------- */

  MenuBuilder.prototype.handleLogo = function (file) {
    var self = this;
    if (!file) return;

    var maxKb = parseInt(this.nodes.logoInput.dataset.maxKb, 10) || 2048;
    this.clearError('logo');

    if (file.type !== 'image/png') {
      this.showError('logo', 'Please upload a PNG file.');
      this.nodes.logoInput.value = '';
      return;
    }

    if (file.size > maxKb * 1024) {
      this.showError('logo', 'That file is ' + Math.round(file.size / 1024) + 'kb. The limit is ' + maxKb + 'kb.');
      this.nodes.logoInput.value = '';
      return;
    }

    var minPx = parseInt(this.nodes.logoInput.dataset.minPx, 10) || 300;
    var reader = new FileReader();
    reader.onload = function () {
      /* Checked here, not just at print time: a logo too small to print
         cleanly on A5 should be rejected while the customer can still pick a
         different file, not silently baked into a PDF later. */
      var probe = new Image();
      probe.onload = function () {
        if (probe.naturalWidth < minPx || probe.naturalHeight < minPx) {
          self.showError('logo', 'That image is ' + probe.naturalWidth + '×' + probe.naturalHeight +
            'px. Please upload one at least ' + minPx + '×' + minPx + 'px.');
          self.nodes.logoInput.value = '';
          return;
        }
        Transport.uploadLogo(file, reader.result).then(function (result) {
          self.state.logo = { url: result.url, name: result.name };
          self.paintLogo();
          self.commit();
        }).catch(function () {
          self.showError('logo', 'The logo could not be uploaded. Please try again.');
        });
      };
      probe.onerror = function () {
        self.showError('logo', 'That file could not be read. Please try again.');
        self.nodes.logoInput.value = '';
      };
      probe.src = reader.result;
    };
    reader.onerror = function () {
      self.showError('logo', 'That file could not be read. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  MenuBuilder.prototype.clampLogoScale = function (value) {
    var min = this.nodes.logoScale ? parseInt(this.nodes.logoScale.min, 10) || 50 : 50;
    var max = this.nodes.logoScale ? parseInt(this.nodes.logoScale.max, 10) || 200 : 200;
    if (isNaN(value)) value = 100;
    return Math.max(min, Math.min(max, value));
  };

  MenuBuilder.prototype.paintLogo = function () {
    var has = !!(this.state.logo && this.state.logo.url);
    if (this.nodes.logoClear) this.nodes.logoClear.hidden = !has;
    if (this.nodes.logoLabel && has) this.nodes.logoLabel.textContent = this.state.logo.name || 'Logo uploaded';

    var scale = this.clampLogoScale(this.state.logoScale);
    this.state.logoScale = scale;
    if (this.nodes.logoSizeField) this.nodes.logoSizeField.hidden = !has;
    if (this.nodes.logoScale && String(this.nodes.logoScale.value) !== String(scale)) {
      this.nodes.logoScale.value = String(scale);
    }
    if (this.nodes.logoScaleOut) this.nodes.logoScaleOut.textContent = scale + '%';

    var show = false;
    if (this.nodes.logoSlot) {
      /* Where the logo sits is the design's decision, not ours: every supplied
         template puts it on the cover only, but the ticket allows for both. */
      var design = this.design();
      var placement = (design && design.logoPlacement) || 'front';
      var onThisSide = placement === 'both' ||
        placement === (this.state.side === 'back' ? 'back' : 'front');
      show = has && onThisSide;
      this.nodes.logoSlot.hidden = !show;
      this.nodes.logoSlot.innerHTML = '';
      this.nodes.logoSlot.style.setProperty('--mb-logo-scale', String(scale / 100));
      if (has) {
        var img = document.createElement('img');
        img.src = this.state.logo.url;
        img.alt = '';
        this.nodes.logoSlot.appendChild(img);
      }
    }
    this.paintLogoMask(show);
    if (has) this.primePrintMask();
  };

  /**
   * Each supplied plate has "LOGO HERE" baked into the artwork as a positioning
   * guide, so a real logo drawn over it left the placeholder showing around and
   * behind it. The text cannot be removed from the image, so a patch is laid
   * over the placeholder's own box, filled with the colour the artwork has
   * around that spot and feathered at the edge. The colour is read off the
   * plate itself (the four inner corners of the box, which never touch the
   * text) so it adapts to all six designs, gradients included, with no
   * per-design setting to keep in step. If the image cannot be read back (a
   * cross-origin canvas taint) it falls back to the design's plate colour.
   */
  MenuBuilder.prototype.paintLogoMask = function (show) {
    var mask = this.nodes.logoMask;
    if (!mask) return;
    mask.hidden = !show;
    if (!show) return;

    var menu = this.root.querySelector('[data-mb-menu]');
    var art = this.nodes.art;
    var src = art && !art.hidden ? art.getAttribute('src') : '';
    var cs = menu ? window.getComputedStyle(menu) : null;
    var geo = cs ? {
      x: parseFloat(cs.getPropertyValue('--mb-logo-x')),
      y: parseFloat(cs.getPropertyValue('--mb-logo-y')),
      w: parseFloat(cs.getPropertyValue('--mb-logo-w')),
      h: parseFloat(cs.getPropertyValue('--mb-logo-h'))
    } : null;

    var plate = 'rgb(var(--mb-plate))';
    var setColours = function (top, bottom, mid) {
      mask.style.setProperty('--mb-mask-top', top);
      mask.style.setProperty('--mb-mask-bottom', bottom);
      mask.style.setProperty('--mb-mask-mid', mid);
    };

    var key = src + '|' + (geo ? [geo.x, geo.y, geo.w, geo.h].join(',') : '');
    if (this.maskKey === key) return;
    this.maskKey = key;
    setColours(plate, plate, plate);

    if (!src || !geo || [geo.x, geo.y, geo.w, geo.h].some(isNaN)) return;

    var self = this;
    this.sampleMaskColours(src, geo, function (colours) {
      if (self.maskKey !== key || !colours) return;
      setColours(colours.top, colours.bottom, colours.mid);
    });
  };

  /** The design's logo box as percentages of the plate, or null before a
      design is shown. Set per design by sc-menu-builder.liquid. */
  MenuBuilder.prototype.logoGeometry = function () {
    var menu = this.root.querySelector('[data-mb-menu]');
    if (!menu) return null;
    var cs = window.getComputedStyle(menu);
    var geo = {
      x: parseFloat(cs.getPropertyValue('--mb-logo-x')),
      y: parseFloat(cs.getPropertyValue('--mb-logo-y')),
      w: parseFloat(cs.getPropertyValue('--mb-logo-w')),
      h: parseFloat(cs.getPropertyValue('--mb-logo-h'))
    };
    return [geo.x, geo.y, geo.w, geo.h].some(isNaN) ? null : geo;
  };

  /**
   * Reads the artwork's own colours around the placeholder (see paintLogoMask)
   * and calls back with { top, bottom, mid } as rgb() strings, or null if the
   * image cannot be read back.
   */
  MenuBuilder.prototype.sampleMaskColours = function (src, geo, done) {
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var W = probe.naturalWidth;
        var H = probe.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);

        /* Same 90% x 60% box as .th-mb__menu-logo-mask in the CSS. */
        var mw = geo.w * 0.9;
        var mh = geo.h * 0.6;
        var left = (geo.x - mw / 2) / 100 * W;
        var right = (geo.x + mw / 2) / 100 * W;
        var top = (geo.y - mh / 2) / 100 * H;
        var bottom = (geo.y + mh / 2) / 100 * H;
        var insetX = (right - left) * 0.06;
        var insetY = (bottom - top) * 0.12;
        var size = Math.max(3, Math.round(W / 150));

        var avg = function (cx, cy) {
          var x = Math.max(0, Math.min(W - size, Math.round(cx - size / 2)));
          var y = Math.max(0, Math.min(H - size, Math.round(cy - size / 2)));
          var d = ctx.getImageData(x, y, size, size).data;
          var r = 0, g = 0, b = 0, n = 0;
          for (var i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 8) continue;
            r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
          }
          return n ? [r / n, g / n, b / n] : null;
        };
        var mix = function (a, b) {
          if (!a || !b) return a || b;
          return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
        };
        var css = function (c) {
          return 'rgb(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ')';
        };

        var tl = avg(left + insetX, top + insetY);
        var tr = avg(right - insetX, top + insetY);
        var bl = avg(left + insetX, bottom - insetY);
        var br = avg(right - insetX, bottom - insetY);
        var topC = mix(tl, tr);
        var bottomC = mix(bl, br);
        if (!topC || !bottomC) return done(null);
        done({ top: css(topC), bottom: css(bottomC), mid: css(mix(topC, bottomC)) });
      } catch (e) {
        /* Tainted canvas or unreadable image. */
        done(null);
      }
    };
    probe.onerror = function () { done(null); };
    probe.src = src;
  };

  /**
   * The print service lays the client's front artwork down itself, so it needs
   * the colours that hide the baked-in "LOGO HERE" guide too. They are sampled
   * for the FRONT artwork whenever a logo is set, whichever side is on screen,
   * and cached for payload().
   */
  MenuBuilder.prototype.primePrintMask = function () {
    var design = this.design();
    var geo = this.logoGeometry();
    if (!design || !design.front || !geo) return;
    var key = design.front + '|' + [geo.x, geo.y, geo.w, geo.h].join(',');
    if (this.printMaskKey === key) return;
    this.printMaskKey = key;
    this.printMask = null;
    var self = this;
    this.sampleMaskColours(design.front, geo, function (colours) {
      if (self.printMaskKey === key) self.printMask = colours;
    });
  };

  /** A design colour token ("17, 17, 17" or "17 17 17") as [r, g, b], or null. */
  MenuBuilder.prototype.designColour = function (token) {
    var menu = this.root.querySelector('[data-mb-menu]');
    if (!menu) return null;
    var parts = (window.getComputedStyle(menu).getPropertyValue(token).match(/\d+(\.\d+)?/g) || []).map(Number);
    return parts.length >= 3 ? parts.slice(0, 3) : null;
  };

  /** The artwork URL at its master size: the preview asks Shopify for 900px. */
  MenuBuilder.prototype.printArtUrl = function (url) {
    if (!url) return null;
    try {
      var u = new URL(url, window.location.href);
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      return u.href;
    } catch (e) {
      return url;
    }
  };

  /* ------------------------------ drinks -------------------------------- */

  /** Anything a customer would be upset to lose silently, short of the
      completeness `drinkComplete` requires before Send. */
  MenuBuilder.prototype.drinkHasContent = function (drink) {
    return !!(drink.name || drink.description || drink.ingredients || drink.price ||
      (drink.products && drink.products.length) || (drink.flavours && drink.flavours.length));
  };

  MenuBuilder.prototype.setDrinkCount = function (count) {
    count = Math.max(0, Math.min(count, this.maxDrinks));
    var drinks = this.state.drinks;

    /* FC001P-45: "If the user reduces the number after adding content, warn
       them before removing any affected drinks." Only the drinks past the
       new count are ever at risk — growth never drops anything. */
    if (count < drinks.length) {
      var atRisk = drinks.slice(count).filter(this.drinkHasContent);
      if (atRisk.length && !window.confirm(
        'Reducing to ' + count + (count === 1 ? ' drink' : ' drinks') +
        ' will remove ' + atRisk.length + (atRisk.length === 1 ? ' drink you' : ' drinks you') +
        ' already started. Continue?'
      )) {
        if (this.nodes.count) this.nodes.count.value = String(this.state.drinkCount);
        return;
      }
    }

    /* Reducing the count must not wipe what was typed into the drinks that
       stay. Grow by appending blanks, shrink by trimming the tail only. */
    while (drinks.length < count) drinks.push(this.blankDrink());
    if (drinks.length > count) drinks.length = count;

    this.state.drinkCount = count;
    this.clearError('count');
    this.renderDrinks();
    this.commit();
  };

  MenuBuilder.prototype.drinkComplete = function (drink) {
    return !!(drink.name && drink.description && PRICE_RE.test(drink.price));
  };

  MenuBuilder.prototype.allDrinksValid = function (report) {
    if (!this.state.drinkCount) {
      if (report) this.showError('count', 'Choose how many drinks your menu has.');
      return false;
    }
    for (var i = 0; i < this.state.drinks.length; i++) {
      if (!this.drinkComplete(this.state.drinks[i])) {
        if (report) this.openDrink(i, true);
        return false;
      }
    }
    return true;
  };

  MenuBuilder.prototype.renderDrinks = function () {
    var self = this;
    var host = this.nodes.drinks;
    if (!host) return;

    host.innerHTML = '';

    this.state.drinks.forEach(function (drink, index) {
      host.appendChild(self.buildDrink(drink, index));
    });

    var openIndex = this.state.drinks.findIndex(function (d) {
      return !self.drinkComplete(d);
    });
    if (this.state.drinks.length) this.openDrink(openIndex === -1 ? 0 : openIndex);
  };

  MenuBuilder.prototype.buildDrink = function (drink, index) {
    var self = this;
    var isLast = index === this.state.drinks.length - 1;

    var item = document.createElement('div');
    item.className = 'th-mb__drink';
    item.dataset.mbDrink = String(index);

    var head = document.createElement('button');
    head.type = 'button';
    head.className = 'th-mb__drink-head';
    head.setAttribute('aria-expanded', 'false');

    var title = document.createElement('span');
    title.className = 'th-mb__drink-title th-h5';
    title.textContent = 'Drink ' + (index + 1);
    head.appendChild(title);

    var tick = document.createElement('span');
    tick.className = 'th-mb__drink-tick';
    tick.setAttribute('aria-hidden', 'true');
    head.appendChild(tick);

    head.addEventListener('click', function () {
      var open = item.classList.contains('is-open');
      self.openDrink(open ? -1 : index);
    });

    var body = document.createElement('div');
    body.className = 'th-mb__drink-body';

    body.appendChild(this.buildCocktailField(drink, index));
    body.appendChild(this.buildDescriptionField(drink, index));
    body.appendChild(
      this.buildTextField('Ingredients / garnish', 'E.g vodka, passionfruit, vanilla', drink.ingredients, function (value) {
        drink.ingredients = value;
        self.commit();
      }, this.ingredientsLimit)
    );

    var row = document.createElement('div');
    row.className = 'th-mb__row';
    row.appendChild(this.buildPriceField(drink, index));
    body.appendChild(row);

    /* Products and icons are full-width fields, not price-row companions: both
       hold a chip list that wraps, and both need room for a search panel. */
    body.appendChild(this.buildProductsField(drink));
    if (this.state.flavourKey) body.appendChild(this.buildFlavourField(drink));

    if (!isLast) {
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'th-mb__btn th-mb__btn--primary th-mb__next th-button-label';
      next.textContent = 'Next drink';
      next.addEventListener('click', function () {
        /* Validate before advancing, per the ticket. Anything already typed
           stays put; only the open panel moves. */
        if (!self.validateDrink(index)) return;
        self.openDrink(index + 1);
      });
      body.appendChild(next);
    }

    item.appendChild(head);
    item.appendChild(body);
    this.paintDrinkState(item, drink);
    return item;
  };

  MenuBuilder.prototype.buildCocktailField = function (drink, index) {
    var self = this;
    var field = document.createElement('div');
    field.className = 'th-mb__field';

    var label = document.createElement('span');
    label.className = 'th-mb__label';
    label.textContent = 'Choose a cocktail*';
    field.appendChild(label);

    var combo = document.createElement('div');
    combo.className = 'th-mb__combo';
    combo.dataset.mbCombo = String(index);

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'th-mb__combo-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.textContent = drink.name || 'Choose a cocktail or create your own';
    if (!drink.name) trigger.classList.add('is-placeholder');

    var panel = document.createElement('div');
    panel.className = 'th-mb__combo-panel';
    panel.hidden = true;

    var search = document.createElement('input');
    search.type = 'search';
    search.className = 'th-mb__combo-search';
    search.placeholder = 'Search cocktails';
    search.setAttribute('aria-label', 'Search cocktails');

    var list = document.createElement('ul');
    list.className = 'th-mb__combo-list';
    list.setAttribute('role', 'listbox');

    function localMatches(needle) {
      var n = needle.toLowerCase();
      return self.cocktails.filter(function (c) {
        return !n || c.name.toLowerCase().indexOf(n) !== -1;
      });
    }

    function pick(cocktail) {
      drink.cocktailId = cocktail.id;
      /* The limit is a physical constraint of the printed area, so it binds
         autofilled names too, not just typed ones. */
      drink.name = (cocktail.name || '').slice(0, self.nameLimit);
      drink.custom = false;
      /* Autofill is a copy. Editing it here never writes back to the source
         article, which the ticket calls out explicitly. */
      drink.description = (cocktail.description || '').slice(0, self.descriptionLimit);
      drink.ingredients = (cocktail.ingredients || '').slice(0, self.ingredientsLimit);
      close();
      self.renderDrinks();
      self.openDrink(index);
      self.commit();
    }

    function createYourOwn() {
      drink.cocktailId = CREATE_YOUR_OWN;
      drink.name = '';
      drink.custom = true;
      drink.description = '';
      drink.ingredients = '';
      close();
      self.renderDrinks();
      self.openDrink(index);
      self.commit();
    }

    function noteRow(message) {
      var li = document.createElement('li');
      li.className = 'th-mb__combo-empty th-p5';
      li.textContent = message;
      return li;
    }

    function renderMatches(matches, note) {
      list.innerHTML = '';
      if (note) list.appendChild(noteRow(note));
      matches.forEach(function (cocktail) {
        list.appendChild(buildOption(cocktail.name, function () { pick(cocktail); }));
      });
      list.appendChild(buildOption('Create your own', createYourOwn,
        'th-mb__combo-option--create'));
    }

    /* Only the newest keystroke may paint. Without this a slow response for
       "mar" can land after "margarita" and overwrite the better list. */
    var searchToken = 0;

    function paintList(term) {
      var needle = (term || '').trim();
      var token = ++searchToken;

      /* One character is not worth a request, and the inline payload answers
         an empty box instantly. */
      if (needle.length < 2) {
        renderMatches(localMatches(needle));
        return;
      }

      renderMatches(localMatches(needle), 'Searching all recipes\u2026');

      self.searchCocktails(needle).then(function (results) {
        if (token !== searchToken) return;
        renderMatches(results, results.length ? null
          : 'No cocktails match \u201c' + needle + '\u201d.');
      })['catch'](function () {
        /* Search being down must not strand the customer: fall back to the
           inline payload and say plainly that the list is partial. */
        if (token !== searchToken) return;
        var local = localMatches(needle);
        renderMatches(local, local.length
          ? 'Showing recent recipes only, search is unavailable.'
          : 'Search is unavailable. Create your own instead.');
      });
    }

    function buildOption(text, onPick, extraClass) {
      var li = document.createElement('li');
      li.className = 'th-mb__combo-option' + (extraClass ? ' ' + extraClass : '');
      li.setAttribute('role', 'option');
      li.tabIndex = 0;
      li.textContent = text;
      li.addEventListener('click', onPick);
      li.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPick();
        }
      });
      return li;
    }

    function open() {
      self.closeAllDropdowns();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      combo.classList.add('is-open');
      paintList(search.value);
      search.focus();
    }

    function close() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      combo.classList.remove('is-open');
    }

    trigger.addEventListener('click', function () {
      if (panel.hidden) open();
      else close();
    });

    var debounce = null;
    search.addEventListener('input', function () {
      var value = this.value;
      if (debounce) clearTimeout(debounce);
      /* Type-ahead against a 356-article corpus, so the request waits for a
         pause rather than firing on every character. */
      debounce = setTimeout(function () { paintList(value); }, 220);
    });

    combo.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
        trigger.focus();
      }
    });

    panel.appendChild(search);
    panel.appendChild(list);
    combo.appendChild(trigger);
    combo.appendChild(panel);
    field.appendChild(combo);

    if (drink.custom) {
      field.appendChild(
        this.buildTextField('Cocktail name*', 'E.g house spritz', drink.name, function (value) {
          drink.name = value;
          self.clearError('drink-' + index + '-name');
          self.commit();
        }, this.nameLimit)
      );
    }

    field.appendChild(this.errorNode('drink-' + index + '-name'));
    return field;
  };

  MenuBuilder.prototype.buildDescriptionField = function (drink, index) {
    var self = this;
    var field = document.createElement('div');
    field.className = 'th-mb__field';

    var label = document.createElement('label');
    label.className = 'th-mb__label';
    label.textContent = 'Drink description*';
    field.appendChild(label);

    var wrap = document.createElement('div');
    wrap.className = 'th-mb__input-wrap th-mb__input-wrap--counted';

    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'th-mb__input';
    input.placeholder = 'E.g fruity, tart, sweet';
    input.maxLength = this.descriptionLimit;
    input.value = drink.description || '';
    label.setAttribute('for', (input.id = 'th-mb-desc-' + index));

    var counter = document.createElement('span');
    counter.className = 'th-mb__counter th-s3';
    counter.textContent = (drink.description || '').length + '/' + this.descriptionLimit;

    input.addEventListener('input', function () {
      drink.description = this.value;
      counter.textContent = this.value.length + '/' + self.descriptionLimit;
      self.clearError('drink-' + index + '-description');
      self.commit();
    });

    wrap.appendChild(input);
    wrap.appendChild(counter);
    field.appendChild(wrap);
    field.appendChild(this.errorNode('drink-' + index + '-description'));
    return field;
  };

  MenuBuilder.prototype.buildPriceField = function (drink, index) {
    var self = this;
    var field = document.createElement('div');
    field.className = 'th-mb__field th-mb__field--price';

    var label = document.createElement('label');
    label.className = 'th-mb__label';
    label.textContent = 'Price*';
    field.appendChild(label);

    var wrap = document.createElement('div');
    wrap.className = 'th-mb__input-wrap th-mb__input-wrap--price';

    var symbol = document.createElement('span');
    symbol.className = 'th-mb__price-symbol';
    symbol.textContent = '£';

    var input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.className = 'th-mb__input';
    input.placeholder = '10.00';
    input.value = drink.price || '';
    label.setAttribute('for', (input.id = 'th-mb-price-' + index));

    input.addEventListener('input', function () {
      drink.price = this.value;
      self.clearError('drink-' + index + '-price');
      self.commit();
    });

    input.addEventListener('blur', function () {
      if (PRICE_RE.test(this.value)) {
        drink.price = money(this.value);
        this.value = drink.price;
        self.commit();
      }
    });

    wrap.appendChild(symbol);
    wrap.appendChild(input);
    field.appendChild(wrap);
    field.appendChild(this.errorNode('drink-' + index + '-price'));
    return field;
  };

  /**
   * Multi-select picker shared by the Funkin product selector and the flavour
   * icon set. One primitive, not two near-identical combos: both pick up to N
   * items from a merchant-approved list, show what is chosen, allow removal,
   * and exist because content has to stay inside a fixed print area.
   *
   * The chips repaint in place rather than through renderDrinks(), which would
   * tear down the open panel and the focused search on every pick.
   */
  MenuBuilder.prototype.buildChooser = function (config) {
    var self = this;
    var chosen = config.chosen;

    var field = document.createElement('div');
    field.className = 'th-mb__field ' + (config.fieldClass || '');

    var label = document.createElement('span');
    label.className = 'th-mb__label';
    label.textContent = config.label;
    field.appendChild(label);

    if (config.hint) {
      var hint = document.createElement('span');
      hint.className = 'th-mb__hint th-p5';
      hint.textContent = config.hint;
      field.appendChild(hint);
    }

    var chips = document.createElement('ul');
    chips.className = 'th-mb__chips';
    chips.setAttribute('role', 'list');
    field.appendChild(chips);

    var combo = document.createElement('div');
    combo.className = 'th-mb__combo';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'th-mb__combo-trigger is-placeholder';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    var panel = document.createElement('div');
    panel.className = 'th-mb__combo-panel';
    panel.hidden = true;

    var search = null;
    if (config.searchable) {
      search = document.createElement('input');
      search.type = 'search';
      search.className = 'th-mb__combo-search';
      search.placeholder = config.searchPlaceholder || 'Search';
      search.setAttribute('aria-label', config.searchPlaceholder || 'Search');
      panel.appendChild(search);
    }

    var optionList = document.createElement('ul');
    optionList.className = 'th-mb__combo-list';
    optionList.setAttribute('role', 'listbox');
    optionList.setAttribute('aria-multiselectable', 'true');
    panel.appendChild(optionList);

    function isChosen(id) {
      return chosen.indexOf(String(id)) !== -1;
    }

    function atCap() {
      return chosen.length >= config.max;
    }

    function toggle(id) {
      id = String(id);
      var at = chosen.indexOf(id);
      if (at === -1) {
        /* Silently ignoring a pick at the cap would read as a broken control,
           so the option is disabled before it can be clicked. */
        if (atCap()) return;
        chosen.push(id);
      } else {
        chosen.splice(at, 1);
      }
      paintChips();
      paintTrigger();
      /* Repaint the rows in place. Rebuilding the list here would detach the
         <li> that was just clicked, and the document-level "click outside
         closes the dropdown" guard tests root.contains(event.target) AFTER
         this handler returns: a detached node is not contained, so every
         dropdown would close on the first pick. */
      paintOptionStates();
      self.commit();
    }

    function paintTrigger() {
      var full = atCap();
      trigger.textContent = full
        ? (config.capText || 'Maximum reached') + ' (' + chosen.length + '/' + config.max + ')'
        : config.triggerText;
      trigger.disabled = full;
      trigger.classList.toggle('is-placeholder', !full);
      if (full && !panel.hidden) close();
    }

    function paintChips() {
      chips.innerHTML = '';
      chips.hidden = !chosen.length;

      chosen.forEach(function (id) {
        var item = findById(config.items, id);
        if (!item) return;

        var li = document.createElement('li');
        li.className = 'th-mb__chip';

        if (config.withIcons && item.icon) {
          var img = document.createElement('img');
          img.className = 'th-mb__chip-icon';
          img.src = item.icon;
          img.alt = '';
          img.width = 20;
          img.height = 20;
          li.appendChild(img);
        }

        var text = document.createElement('span');
        text.className = 'th-mb__chip-text';
        text.textContent = item.name;
        li.appendChild(text);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'th-mb__chip-remove';
        remove.setAttribute('aria-label', 'Remove ' + item.name);
        remove.textContent = '\u00d7';
        remove.addEventListener('click', function () {
          toggle(id);
        });
        li.appendChild(remove);

        chips.appendChild(li);
      });
    }

    function paintList(term) {
      optionList.innerHTML = '';
      var needle = (term || '').trim().toLowerCase();

      var matches = config.items.filter(function (item) {
        return !needle || item.name.toLowerCase().indexOf(needle) !== -1;
      });

      if (!config.items.length) {
        optionList.appendChild(emptyRow(config.noneText || 'Nothing available.'));
        return;
      }

      if (!matches.length) {
        optionList.appendChild(emptyRow('Nothing matches \u201c' + term + '\u201d.'));
        return;
      }

      matches.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'th-mb__combo-option th-mb__combo-option--multi';
        li.setAttribute('role', 'option');
        li.dataset.mbOption = String(item.id);

        var box = document.createElement('span');
        box.className = 'th-mb__combo-box';
        box.setAttribute('aria-hidden', 'true');
        li.appendChild(box);

        if (config.withIcons && item.icon) {
          var img = document.createElement('img');
          img.className = 'th-mb__combo-icon';
          img.src = item.icon;
          img.alt = '';
          img.width = 24;
          img.height = 24;
          li.appendChild(img);
        }

        var text = document.createElement('span');
        text.className = 'th-mb__combo-text';
        text.textContent = item.name;
        li.appendChild(text);

        /* Listeners are attached once and guard on the live state, rather than
           being added and removed as the cap moves. That keeps every row a
           stable node for the lifetime of the list. */
        li.addEventListener('click', function () {
          if (li.classList.contains('is-locked')) return;
          toggle(item.id);
        });
        li.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          if (li.classList.contains('is-locked')) return;
          toggle(item.id);
        });

        optionList.appendChild(li);
      });

      paintOptionStates();
    }

    /**
     * Selected and locked states, painted onto the rows that already exist.
     * A chosen row stays clickable at the cap so it can be removed; only
     * unchosen rows lock.
     */
    function paintOptionStates() {
      var full = atCap();
      Array.prototype.forEach.call(optionList.querySelectorAll('[data-mb-option]'), function (li) {
        var picked = isChosen(li.dataset.mbOption);
        var locked = !picked && full;
        li.classList.toggle('is-picked', picked);
        li.classList.toggle('is-locked', locked);
        li.setAttribute('aria-selected', String(picked));
        li.tabIndex = locked ? -1 : 0;
      });
    }

    function emptyRow(message) {
      var li = document.createElement('li');
      li.className = 'th-mb__combo-empty th-p5';
      li.textContent = message;
      return li;
    }

    function open() {
      self.closeAllDropdowns();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      combo.classList.add('is-open');
      paintList(search ? search.value : '');
      if (search) search.focus();
    }

    function close() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      combo.classList.remove('is-open');
    }

    trigger.addEventListener('click', function () {
      if (panel.hidden) open();
      else close();
    });

    if (search) {
      search.addEventListener('input', function () {
        paintList(this.value);
      });
    }

    combo.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
        trigger.focus();
      }
    });

    combo.appendChild(trigger);
    combo.appendChild(panel);
    field.appendChild(combo);

    paintChips();
    paintTrigger();
    return field;
  };

  MenuBuilder.prototype.buildProductsField = function (drink) {
    return this.buildChooser({
      fieldClass: 'th-mb__field--products',
      label: this.text('productsLabel', 'Funkin products'),
      hint: this.text('productsHint', 'Choose the Funkin products used in this drink.'),
      items: this.products,
      chosen: drink.products,
      max: this.maxProducts,
      searchable: true,
      searchPlaceholder: 'Search Funkin products',
      triggerText: 'Add a product',
      capText: 'All products chosen',
      noneText: 'No Funkin products are available for the builder yet.'
    });
  };

  MenuBuilder.prototype.buildFlavourField = function (drink) {
    return this.buildChooser({
      fieldClass: 'th-mb__field--flavour',
      label: this.text('flavourLabel', 'Flavour icons'),
      hint: this.text('flavourHint', 'Optional. These appear against the drink and in the key.'),
      items: this.flavours,
      chosen: drink.flavours,
      max: this.maxFlavours,
      searchable: false,
      withIcons: true,
      triggerText: 'Add a flavour icon',
      capText: 'All icons chosen',
      noneText: 'No flavour icons have been set up yet.'
    });
  };

  /** Merchant-editable strings ride on the root as data attributes so the
      section schema stays the single place copy is changed. */
  MenuBuilder.prototype.text = function (key, fallback) {
    var value = this.root.dataset[key];
    return value === undefined || value === '' ? fallback : value;
  };

  MenuBuilder.prototype.buildTextField = function (labelText, placeholder, value, onInput, limit) {
    var field = document.createElement('div');
    field.className = 'th-mb__field';

    var label = document.createElement('label');
    label.className = 'th-mb__label';
    label.textContent = labelText;
    field.appendChild(label);

    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'th-mb__input';
    input.placeholder = placeholder;
    input.value = value || '';

    if (!limit) {
      input.addEventListener('input', function () {
        onInput(this.value);
      });
      field.appendChild(input);
      return field;
    }

    /* Same shape as the description field: a hard maxLength plus a live
       counter, because a limit the customer cannot see reads as a bug when
       typing stops. */
    input.maxLength = limit;

    var wrap = document.createElement('div');
    wrap.className = 'th-mb__input-wrap th-mb__input-wrap--counted';

    var counter = document.createElement('span');
    counter.className = 'th-mb__counter th-s3';
    counter.textContent = (value || '').length + '/' + limit;

    input.addEventListener('input', function () {
      counter.textContent = this.value.length + '/' + limit;
      onInput(this.value);
    });

    wrap.appendChild(input);
    wrap.appendChild(counter);
    field.appendChild(wrap);
    return field;
  };

  MenuBuilder.prototype.errorNode = function (key) {
    var p = document.createElement('p');
    p.className = 'th-mb__error';
    p.dataset.mbErrorFor = key;
    p.setAttribute('role', 'alert');
    p.hidden = true;
    return p;
  };

  MenuBuilder.prototype.openDrink = function (index, reportErrors) {
    var items = this.root.querySelectorAll('[data-mb-drink]');
    Array.prototype.forEach.call(items, function (item) {
      var isTarget = parseInt(item.dataset.mbDrink, 10) === index;
      item.classList.toggle('is-open', isTarget);
      var head = item.querySelector('.th-mb__drink-head');
      if (head) head.setAttribute('aria-expanded', String(isTarget));
    });
    if (reportErrors && index >= 0) this.validateDrink(index);
  };

  MenuBuilder.prototype.validateDrink = function (index) {
    var drink = this.state.drinks[index];
    if (!drink) return false;
    var ok = true;

    if (!drink.name) {
      this.showError('drink-' + index + '-name', 'Choose a cocktail or create your own.');
      ok = false;
    }
    if (!drink.description) {
      this.showError('drink-' + index + '-description', 'Add a short description.');
      ok = false;
    }
    if (!PRICE_RE.test(drink.price)) {
      this.showError('drink-' + index + '-price', 'Enter a price, for example 10.00.');
      ok = false;
    }

    this.paintDrinkState(this.root.querySelector('[data-mb-drink="' + index + '"]'), drink);
    return ok;
  };

  MenuBuilder.prototype.paintDrinkState = function (item, drink) {
    if (!item) return;
    item.classList.toggle('is-complete', this.drinkComplete(drink));
  };

  /* ------------------------------ preview ------------------------------- */

  MenuBuilder.prototype.renderPreview = function () {
    var design = this.design();

    var isBack = this.state.side === 'back';

    if (this.nodes.art) {
      /* Each face shows its OWN artwork or none at all. Falling back to the
         front image on the back was why the Front/Back toggle appeared to do
         nothing: with no back plate configured, both sides painted the cover.
         The supplied back pages are filled comps and must not be used here, so
         until design sends empty plates the back correctly has no image and
         the plate colour shows behind the typeset menu. */
      var src = '';
      if (design) src = isBack ? this.backFace(design, this.state.drinkCount) : design.front;
      if (src) {
        /* Artwork is a BACKGROUND only: the drink rows are typeset over it.
           A back plate that still has drinks printed on it would double up,
           which is why the supplied comps must not be used here. A missing or
           broken file hides rather than leaving a broken-image glyph on the
           plate. */
        this.nodes.art.onerror = function () { this.hidden = true; };
        this.nodes.art.src = src;
        this.nodes.art.hidden = false;
      } else {
        this.nodes.art.removeAttribute('src');
        this.nodes.art.hidden = true;
      }
    }

    /* Drinks and the flavour key belong to the BACK of the menu. The front is
       the cover: artwork and the venue logo. */
    if (this.nodes.slots) {
      this.nodes.slots.hidden = !isBack;
      this.nodes.slots.innerHTML = '';
      for (var i = 0; i < this.state.drinkCount; i++) {
        var drink = this.state.drinks[i] || this.blankDrink();
        var li = document.createElement('li');
        li.className = 'th-mb__slot' + (this.drinkComplete(drink) ? ' is-filled' : '');

        /* Typeset, not abstract rules. The supplied plates are flattened
           comps with placeholder drinks baked in, so they cannot be used as
           a background to overlay: the menu body is rendered from the same
           geometry the PDFs were measured at. */
        var name = document.createElement('span');
        name.className = 'th-mb__slot-name';
        name.textContent = drink.name || '';
        li.appendChild(name);

        var ing = document.createElement('span');
        ing.className = 'th-mb__slot-ing';
        ing.textContent = this.printedLine(drink);
        li.appendChild(ing);

        var price = document.createElement('span');
        price.className = 'th-mb__slot-price';
        price.textContent = money(drink.price) ? '£' + money(drink.price) : '';
        li.appendChild(price);

        /* Products and icons ARE typeset, unlike the name and description
           rules above. Both are selections rather than free text, so they
           cannot overrun the plate at any length the builder allows, and the
           ticket asks for the preview to move the moment either changes. */
        var products = this.printProducts === 'hide' ? [] : this.chosenProducts(drink);
        if (products.length) {
          var plist = document.createElement('ul');
          plist.className = 'th-mb__slot-products';
          plist.setAttribute('role', 'list');
          products.forEach(function (product) {
            var pi = document.createElement('li');
            pi.className = 'th-mb__slot-product';
            pi.textContent = product.name;
            plist.appendChild(pi);
          });
          li.appendChild(plist);
        }

        var flavours = this.chosenFlavours(drink);
        if (flavours.length) {
          var fwrap = document.createElement('span');
          fwrap.className = 'th-mb__slot-flavours';
          flavours.forEach(function (flavour) {
            fwrap.appendChild(flavourMark(flavour, 'th-mb__slot-flavour'));
          });
          li.appendChild(fwrap);
        }

        li.classList.toggle('is-empty', !drink.name && !drink.ingredients && !drink.price);

        this.nodes.slots.appendChild(li);
      }
      this.nodes.slots.dataset.count = String(this.state.drinkCount);
    }

    this.renderKey();
    this.paintDisclaimer();
    this.checkFit();
    this.paintLogo();

    var menu = this.root.querySelector('[data-mb-menu]');
    if (menu) {
      menu.classList.toggle('has-key', !!this.state.flavourKey);
      /* Selects the design's block in the token layer. Unknown slugs simply
         inherit the defaults, which is the correct behaviour for the four
         designs whose faces are still unconfirmed. */
      if (design && design.slug) menu.dataset.design = design.slug;
      else menu.removeAttribute('data-design');

      menu.dataset.rowLayout = design ? design.rowLayout : 'stacked';
      menu.classList.toggle('is-preview-products', this.printProducts === 'preview');

      /* Each design names its own faces, so the ratios are re-measured when the
         design changes, not once at boot. */
      if (menu.dataset.calibratedFor !== (menu.dataset.design || '')) {
        menu.dataset.calibratedFor = menu.dataset.design || '';
        menu.style.removeProperty('--mb-cap-body');
        menu.style.removeProperty('--mb-cap-display');
        this.calibrateType();
      }
    }
  };

  /**
   * Which text prints under the drink name. Every supplied plate shows one line
   * and it reads as the ingredients, but the builder also collects a required
   * description and no plate has a place for it. Merchant-settable rather than
   * assumed, so the answer can change without a release.
   */
  MenuBuilder.prototype.printedLine = function (drink) {
    var ing = (drink.ingredients || '').trim();
    var desc = (drink.description || '').trim();
    if (this.printUnderName === 'description') return desc;
    if (this.printUnderName === 'both') return [desc, ing].filter(Boolean).join(' \u00b7 ');
    return ing;
  };

  /**
   * The key legend lists only the icons actually used, so turning the key on
   * with nothing picked yet does not print an empty strip, and removing the
   * last drink using an icon drops it from the legend. Ordered by the approved
   * list rather than by drink, so the legend is stable as drinks are edited.
   */
  MenuBuilder.prototype.renderKey = function () {
    var node = this.nodes.key;
    if (!node) return;

    node.innerHTML = '';

    if (!this.state.flavourKey || this.state.side !== 'back') {
      node.hidden = true;
      return;
    }

    var used = {};
    this.state.drinks.forEach(function (drink) {
      (drink.flavours || []).forEach(function (id) {
        used[String(id)] = true;
      });
    });

    var entries = this.flavours.filter(function (flavour) {
      return used[String(flavour.id)];
    });

    node.hidden = !entries.length;
    if (!entries.length) return;

    var list = document.createElement('ul');
    list.className = 'th-mb__key-list';
    list.setAttribute('role', 'list');

    entries.forEach(function (flavour) {
      var li = document.createElement('li');
      li.className = 'th-mb__key-item';
      li.appendChild(flavourMark(flavour, 'th-mb__key-icon'));
      var name = document.createElement('span');
      name.className = 'th-mb__key-name';
      name.textContent = flavour.name;
      li.appendChild(name);
      list.appendChild(li);
    });

    node.appendChild(list);
  };

  /* Required, so every menu prints with one: same visibility rule as the
     drinks and key it sits below — back face only. */
  MenuBuilder.prototype.paintDisclaimer = function () {
    var node = this.nodes.disclaimer;
    if (!node) return;
    var text = (this.state.disclaimer || '').trim();
    node.hidden = this.state.side !== 'back' || !text;
    node.textContent = text;
  };

  MenuBuilder.prototype.disclaimerValid = function () {
    return !!(this.state.disclaimer && this.state.disclaimer.trim());
  };

  /**
   * Does the menu still fit its plate? The band clips rather than letting rows
   * slide under the flavour key, so without this the customer would see a
   * tidy-looking plate that is quietly missing drinks.
   */
  MenuBuilder.prototype.checkFit = function () {
    var node = this.nodes.overflow;
    var slots = this.nodes.slots;
    if (!node || !slots) return;
    /* Measure even when the front is showing. Whether a menu fits its plate is
       a property of the content, not of the face being viewed: gating this on
       `side === 'back'` let an over-long menu be submitted from the front. The
       rows are un-hidden for the measurement and re-hidden in the same
       synchronous block, so nothing flashes.

       A one-pixel tolerance: sub-pixel layout rounding is not an overflow. */
    var wasHidden = slots.hidden;
    if (wasHidden) slots.hidden = false;
    var over = slots.scrollHeight - slots.clientHeight > 1;
    if (wasHidden) slots.hidden = true;
    node.hidden = !over;
    slots.classList.toggle('is-overflowing', over);
  };

  /** Resolve stored ids to the live payload entries, dropping anything the
      merchant has retired since the draft was saved. */
  MenuBuilder.prototype.chosenProducts = function (drink) {
    return (drink.products || []).map(function (id) {
      return findById(this.products, id);
    }, this).filter(Boolean);
  };

  MenuBuilder.prototype.chosenFlavours = function (drink) {
    if (!this.state.flavourKey) return [];
    return (drink.flavours || []).map(function (id) {
      return findById(this.flavours, id);
    }, this).filter(Boolean);
  };

  /**
   * Measure the cap height of whichever face actually resolved, and publish it
   * as the `--mb-cap-*` ratio the type scale divides by.
   *
   * The template PDFs give cap heights, not em sizes, so an em size has to be
   * derived. Deriving it from an assumed ratio is how the first render came out
   * roughly half again too large and overflowed the plate: the fallback faces
   * are not the trial faces the artwork was set in, and every face has its own
   * cap ratio. Measuring means the plate is correct on whatever the browser
   * resolved, and correct again the moment the licensed faces are installed,
   * with no numbers to update by hand.
   */
  function capRatio(stack) {
    try {
      var canvas = capRatio.canvas || (capRatio.canvas = document.createElement('canvas'));
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.font = '100px ' + stack;
      var m = ctx.measureText('H');
      if (!m || typeof m.actualBoundingBoxAscent !== 'number') return null;
      var ratio = m.actualBoundingBoxAscent / 100;
      /* A nonsensical measurement means the face never loaded; keep the
         declared default rather than publishing a broken scale. */
      return ratio > 0.4 && ratio < 1 ? ratio : null;
    } catch (e) {
      return null;
    }
  }

  MenuBuilder.prototype.calibrateType = function () {
    var menu = this.root.querySelector('[data-mb-menu]');
    if (!menu || !window.getComputedStyle) return;
    var cs = window.getComputedStyle(menu);

    [['--mb-font-body', '--mb-cap-body'], ['--mb-font-display', '--mb-cap-display']]
      .forEach(function (pair) {
        var stack = cs.getPropertyValue(pair[0]).trim();
        if (!stack) return;
        var ratio = capRatio(stack);
        if (ratio) menu.style.setProperty(pair[1], String(Math.round(ratio * 1000) / 1000));
      });
  };

  /** An approved icon may legitimately have no artwork yet. Falling back to
      an initial keeps the legend and the drink line in step rather than
      silently dropping the flavour. */
  function flavourMark(flavour, className) {
    if (flavour.icon) {
      var img = document.createElement('img');
      img.className = className;
      img.src = flavour.icon;
      img.alt = flavour.name;
      img.width = 16;
      img.height = 16;
      return img;
    }
    var span = document.createElement('span');
    span.className = className + ' ' + className + '--text';
    span.textContent = (flavour.name || '?').charAt(0).toUpperCase();
    span.title = flavour.name;
    return span;
  }

  MenuBuilder.prototype.paintSides = function () {
    var side = this.state.side;
    Array.prototype.forEach.call(this.nodes.sides, function (button) {
      var active = button.dataset.mbSide === side;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
  };

  MenuBuilder.prototype.paintQuantity = function () {
    var qty = this.state.quantity;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-qty]'), function (button) {
      var active = parseInt(button.dataset.mbQty, 10) === qty;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
  };

  MenuBuilder.prototype.paintMixers = function () {
    var value = this.state.mixers;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-mb-mixers]'), function (button) {
      var active = button.dataset.mbMixers === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
  };

  MenuBuilder.prototype.closeAllDropdowns = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('.th-mb__combo.is-open'), function (combo) {
      combo.classList.remove('is-open');
      var panel = combo.querySelector('.th-mb__combo-panel');
      var trigger = combo.querySelector('.th-mb__combo-trigger');
      if (panel) panel.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  };

  /* ------------------------------- errors ------------------------------- */

  MenuBuilder.prototype.showError = function (key, message) {
    var node = this.root.querySelector('[data-mb-error-for="' + key + '"]');
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  };

  MenuBuilder.prototype.clearError = function (key) {
    var node = this.root.querySelector('[data-mb-error-for="' + key + '"]');
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
  };

  /* ------------------------------ submit -------------------------------- */

  MenuBuilder.prototype.validateFinal = function () {
    var ok = this.allDrinksValid(true);

    if (!this.state.quantity) {
      this.showError('quantity', 'Choose how many copies you need.');
      ok = false;
    } else if (this.state.quantity > this.maxPerOrder) {
      this.showError('quantity', 'The maximum is ' + this.maxPerOrder + ' menus per order.');
      ok = false;
    }

    if (!this.state.mixers) {
      this.showError('mixers', 'Let us know whether you stock all the mixers.');
      ok = false;
    }

    /* The plate clips, so an overflowing menu would print with drinks missing.
       Block the send rather than generate a PDF nobody checked. */
    this.checkFit();
    if (this.nodes.overflow && !this.nodes.overflow.hidden) {
      this.showError('quantity', 'Your menu is too long for the selected design. Adjust it before sending.');
      ok = false;
    }

    var required = {
      email: 'Enter a contact email.',
      venue: 'Enter the venue name.',
      address: 'Enter the venue address.',
      postcode: 'Enter the venue postcode.',
      country: 'Enter the country.'
    };

    Object.keys(required).forEach(function (key) {
      if (!String(this.state.venue[key] || '').trim()) {
        this.showError(key, required[key]);
        ok = false;
      }
    }, this);

    return ok;
  };

  MenuBuilder.prototype.payload = function () {
    var self = this;
    var design = this.design();
    return {
      requestKey: this.state.requestKey,
      submittedAt: new Date().toISOString(),
      design: design ? {
        id: design.id,
        name: design.name,
        /* Name the layout the customer was previewing. The backend must print
           the same plate, not re-derive it from a rule that could drift. */
        backLayout: this.backFace(design, this.state.drinkCount) || null,
        /* What the print service needs to draw the same menu the customer
           previewed: the client's front artwork at master size, the colours,
           and where the logo sits and what hides its placeholder. */
        front: this.printArtUrl(design.front),
        plate: this.designColour('--mb-plate'),
        ink: this.designColour('--mb-ink'),
        accent: this.designColour('--mb-accent'),
        logoBox: this.logoGeometry(),
        logoMask: this.printMask || null
      } : null,
      logo: this.state.logo ? { name: this.state.logo.name, url: this.state.logo.url, scale: this.state.logoScale / 100 } : null,
      flavourKey: this.state.flavourKey,
      printProducts: this.printProducts,
      printUnderName: this.printUnderName,
      drinkCount: this.state.drinkCount,
      drinks: this.state.drinks.map(function (drink) {
        return {
          cocktailId: drink.custom ? null : drink.cocktailId,
          custom: !!drink.custom,
          name: drink.name,
          description: drink.description,
          ingredients: drink.ingredients,
          price: money(drink.price),
          currency: 'GBP',
          /* 'preview' means the customer sees their choice while building but it
             is not printed, so it travels as sales-team data rather than menu
             content. 'hide' drops it entirely. */
          products: self.printProducts === 'hide' ? [] :
            self.chosenProducts(drink).map(function (product) {
              return { id: product.id, handle: product.handle, name: product.name };
            }),
          /* With the key switched off the icons must not reach the print
             pipeline at all. The choices stay in the local draft so flicking
             the key back on restores them, but they never leave the browser. */
          flavours: self.chosenFlavours(drink).map(function (flavour) {
            return { id: flavour.id, name: flavour.name };
          })
        };
      }),
      quantity: this.state.quantity,
      stocksAllMixers: this.state.mixers === 'yes',
      disclaimer: this.state.disclaimer || '',
      venue: this.state.venue,
      newsletter: {
        consent: !!this.state.newsletter.optIn,
        email: this.state.newsletter.email || ''
      }
    };
  };

  MenuBuilder.prototype.submit = function () {
    var self = this;

    if (this.nodes.success) this.nodes.success.hidden = true;
    if (this.nodes.download) this.nodes.download.hidden = true;
    if (this.nodes.failure) this.nodes.failure.hidden = true;

    if (!this.loggedIn) {
      this.showError('quantity', 'Please sign in before submitting your menu request.');
      return;
    }

    if (!this.validateFinal()) return;

    /* Guard against a double tap producing two requests. */
    if (this.submitting) return;
    this.submitting = true;
    this.nodes.submit.disabled = true;

    Transport.submitMenu(this.payload()).then(function (result) {
      if (self.nodes.success) self.nodes.success.hidden = false;
      /* The backend does not exist yet, so `result` is whatever shape it
         eventually resolves with. `pdfUrl` is a placeholder field name for
         the authorised print-ready PDF link the ticket requires on the
         success screen — confirm the real field name with whoever builds the
         endpoint and adjust here; nothing else in the frontend needs to change. */
      if (self.nodes.download) {
        if (result && result.pdfUrl) {
          self.nodes.download.href = result.pdfUrl;
          self.nodes.download.hidden = false;
        } else {
          self.nodes.download.hidden = true;
        }
      }
      try {
        window.sessionStorage.removeItem(self.storageKey);
      } catch (e) {
        /* Nothing to clean up if storage was never available. */
      }
    }).catch(function () {
      /* The draft is deliberately left in place so a failed send costs the
         customer nothing. */
      if (self.nodes.failure) self.nodes.failure.hidden = false;
    }).then(function () {
      self.submitting = false;
      self.nodes.submit.disabled = false;
    });
  };

  /* ---------------------------------------------------------------------- */

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-menu-builder]'), function (root) {
      if (root.dataset.mbReady === 'true') return;
      root.dataset.mbReady = 'true';
      new MenuBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
